import React, { useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Building2, FileImage, Hash, MapPin, XCircle } from "lucide-react";
import {
  AUTH_CARD_CLASS,
  AUTH_LABEL_CLASS,
  authInputClass,
} from "@/components/auth/authStyles";
import { readImageFileAsDataUrl } from "@/utils/imageFile";
import {
  buildBusinessSignupQuery,
  getBusinessSignupBrImage,
  readBusinessSignupParams,
  setBusinessSignupBrImage,
} from "./businessSignupState";

type FieldKey = "companyName" | "companyAddress" | "brNumber" | "brImage";

const FIELD_ORDER: FieldKey[] = ["companyName", "companyAddress", "brNumber", "brImage"];

const BusinessInformationPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { search } = useLocation();
  const params = readBusinessSignupParams(search);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const companyNameRef = useRef<HTMLInputElement>(null);
  const companyAddressRef = useRef<HTMLInputElement>(null);
  const brNumberRef = useRef<HTMLInputElement>(null);

  const [companyName, setCompanyName] = useState(params.companyName || "");
  const [companyAddress, setCompanyAddress] = useState(params.companyAddress || "");
  const [brNumber, setBrNumber] = useState(params.brNumber || "");
  const [brImage, setBrImage] = useState<string>(getBusinessSignupBrImage);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<FieldKey, string>>>({});
  const [imageError, setImageError] = useState("");

  const FIELD_REFS: Partial<Record<FieldKey, React.RefObject<HTMLInputElement>>> = {
    companyName: companyNameRef,
    companyAddress: companyAddressRef,
    brNumber: brNumberRef,
  };

  const handleChange = (
    key: FieldKey,
    value: string,
    setter: (value: string) => void,
  ) => {
    setter(value);
    setFieldErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
  };

  const pickImage = async (file?: File) => {
    if (!file) return;
    const dataUrl = await readImageFileAsDataUrl(file);
    if (!dataUrl) return;
    setBrImage(dataUrl);
    setFieldErrors((prev) => (prev.brImage ? { ...prev, brImage: undefined } : prev));
    setImageError(
      setBusinessSignupBrImage(dataUrl)
        ? ""
        : t("Could not cache this photo. It will still be uploaded when you register."),
    );
  };

  const onPressContinue = () => {
    const errors: Partial<Record<FieldKey, string>> = {};
    if (!companyName) errors.companyName = "Please fill in all the blank";
    if (!companyAddress) errors.companyAddress = "Please fill in all the blank";
    if (!brNumber) errors.brNumber = "Please fill in all the blank";
    if (!brImage) errors.brImage = "Please upload your business registration certificate";
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      const firstInvalid = FIELD_ORDER.find((key) => errors[key]);
      if (firstInvalid && firstInvalid !== "brImage") {
        FIELD_REFS[firstInvalid]?.current?.focus();
      }
      return;
    }
    setFieldErrors({});
    navigate(
      `/business/signup/plan${buildBusinessSignupQuery({
        ...params,
        companyName,
        companyAddress,
        brNumber,
      })}`,
    );
  };

  const fieldError = (key: FieldKey) => {
    const message = fieldErrors[key];
    if (!message) return null;
    return (
      <p className="mt-1.5 flex items-center gap-1 text-[13px] text-red-500">
        <XCircle size={14} className="shrink-0" />
        {t(message)}
      </p>
    );
  };

  return (
    <div className={AUTH_CARD_CLASS}>
      <h1 className="text-2xl font-extrabold text-[#333]">{t("Business Details")}</h1>

      <div className="mt-6 space-y-4">
        <div>
          <label
            htmlFor="business-company-name"
            className={AUTH_LABEL_CLASS}
          >
            {t("Company Name")}
          </label>
          <div className="relative">
            <Building2
              size={18}
              className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              id="business-company-name"
              ref={companyNameRef}
              type="text"
              value={companyName}
              onChange={(e) => handleChange("companyName", e.target.value, setCompanyName)}
              className={authInputClass(!!fieldErrors.companyName)}
              placeholder={t("Company Name")}
              aria-invalid={!!fieldErrors.companyName}
            />
          </div>
          {fieldError("companyName")}
        </div>
        <div>
          <label
            htmlFor="business-company-address"
            className={AUTH_LABEL_CLASS}
          >
            {t("Company Address")}
          </label>
          <div className="relative">
            <MapPin
              size={18}
              className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              id="business-company-address"
              ref={companyAddressRef}
              type="text"
              value={companyAddress}
              onChange={(e) => handleChange("companyAddress", e.target.value, setCompanyAddress)}
              className={authInputClass(!!fieldErrors.companyAddress)}
              placeholder={t("Company Address")}
              aria-invalid={!!fieldErrors.companyAddress}
            />
          </div>
          {fieldError("companyAddress")}
        </div>
        <div>
          <label
            htmlFor="business-br-number"
            className={AUTH_LABEL_CLASS}
          >
            {t("Business Registration No.")}
          </label>
          <div className="relative">
            <Hash
              size={18}
              className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              id="business-br-number"
              ref={brNumberRef}
              type="text"
              value={brNumber}
              onChange={(e) => handleChange("brNumber", e.target.value, setBrNumber)}
              className={authInputClass(!!fieldErrors.brNumber)}
              placeholder={t("Business Registration No.")}
              aria-invalid={!!fieldErrors.brNumber}
            />
          </div>
          {fieldError("brNumber")}
        </div>

        <div>
          <span className={AUTH_LABEL_CLASS}>
            {t("Business Registration Certificate")}
          </span>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className={`flex h-40 w-full items-center justify-center overflow-hidden rounded-xl border-2 border-dashed bg-[#fff8f0] transition hover:bg-[#fff3e0] ${
              fieldErrors.brImage
                ? "border-red-400"
                : "border-gray-300 hover:border-[#ffa217]"
            }`}
          >
            {brImage ? (
              <img
                src={brImage}
                alt={t("Business Registration Certificate")}
                className="h-full w-full bg-white object-contain"
              />
            ) : (
              <span className="flex flex-col items-center gap-2 text-[#ffa217]">
                <FileImage size={40} />
                <span className="text-sm font-medium text-gray-500">
                  {t("Business Registration Certificate")}
                </span>
              </span>
            )}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => pickImage(e.target.files?.[0])}
          />
          {fieldError("brImage")}
          {imageError && (
            <p className="mt-2 text-center text-sm font-medium text-red-500">
              {imageError}
            </p>
          )}
        </div>

        <button
          type="button"
          onClick={onPressContinue}
          className="w-full rounded-lg bg-[#ffa217] py-3 font-bold text-white transition hover:bg-[#e08f0e]"
        >
          {t("Continue")}
        </button>

        <button
          type="button"
          onClick={() =>
            navigate(`/business/signup/profile${buildBusinessSignupQuery({ ...params })}`)
          }
          className="w-full rounded-lg bg-red-500 py-3 font-bold text-white transition hover:bg-red-600"
        >
          {t("BACK")}
        </button>
      </div>
    </div>
  );
};

export default BusinessInformationPage;
